import { useCallback, useEffect, useState } from 'react';

import { PageTitle } from '@components/PageTitle';
import { Button } from '@components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@components/ui/command';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@components/ui/form';
import { Input } from '@components/ui/input';
import { Label } from '@components/ui/label';
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from '@components/ui/popover';
import { Separator } from '@components/ui/separator';
import { useAuth } from '@contexts/auth';
import errorsValidation from '@errors/validation';
import formatterDecimal from '@formatters/decimal';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@lib/ui/cn';
import BigNumber from 'bignumber.js';
import { Check, ChevronsUpDown, CircleHelp } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { NumberFormatBase } from 'react-number-format';
import { Navigate } from 'react-router';
import { z } from 'zod';

import { useConversorOpioide } from './context';

interface IEquivalence {
  duration: string;
  mg: number;
  name: string;
}

const equivalences: IEquivalence[] = [
  {
    name: 'Morfina VO',
    mg: 1,
    duration: '3-4 horas',
  },
  {
    name: 'Morfina IV',
    mg: 0.333333333333333,
    duration: '3-4 horas',
  },
  {
    name: 'Morfina SC',
    mg: 0.5,
    duration: '3-4 horas',
  },
  {
    name: 'Tramadol VO',
    mg: 7.5,
    duration: '4-6 horas',
  },
  {
    name: 'Tramadol IV',
    mg: 3.3,
    duration: '4-6 horas',
  },
  {
    name: 'Tramadol SC',
    mg: 3.3,
    duration: '4-6 horas',
  },
  {
    name: 'Codeína VO',
    mg: 8,
    duration: '2,5-4 horas',
  },
  {
    name: 'Oxicodona VO',
    mg: 0.666666666666667,
    duration: '3-4,5 horas',
  },
  {
    name: 'Fentanil IV',
    mg: 0.00333333333333333,
    duration: '1-2 horas',
  },
  {
    name: 'Fentanil SC',
    mg: 0.00333333333333333,
    duration: '1-2 horas',
  },
  {
    name: 'Fentanil transdérmico',
    mg: 0.5,
    duration: '20-25 horas',
  },
  {
    name: 'Hidromorfona VO',
    mg: 0.2,
    duration: '2-4 horas',
  },
  {
    name: 'Hidromorfona IV',
    mg: 0.04,
    duration: '2-4 horas',
  },
  {
    name: 'Hidromorfona SC',
    mg: 0.04,
    duration: '2-4 horas',
  },
  {
    name: 'Buprenorfina transdérmica',
    mg: 0.5,
    duration: '~12 horas',
  },
  {
    name: 'Tapentadol VO',
    mg: 2.5,
    duration: '~4 horas',
  },
  {
    name: 'Hidrocodona VO',
    mg: 1,
    duration: '3-4 horas',
  },
  {
    name: 'Metadona (30-90 OMME/dia)',
    mg: 0.25,
    duration: '> 24 horas',
  },
  {
    name: 'Metadona (91-300 OMME/dia)',
    mg: 0.125,
    duration: '> 24 horas',
  },
  {
    name: 'Metadona (301-600 OMME/dia)',
    mg: 0.1,
    duration: '> 24 horas',
  },
  {
    name: 'Metadona (601-800 OMME/dia)',
    mg: 1 / 12,
    duration: '> 24 horas',
  },
  {
    name: 'Metadona (801-1.000 OMME/dia)',
    mg: 1 / 15,
    duration: '> 24 horas',
  },
  {
    name: 'Metadona (> 1.000 OMME/dia)',
    mg: 0.05,
    duration: '> 24 horas',
  },
];

const options = [
  {
    label: 'Buprenorfina transdérmica',
    value: 'Buprenorfina transdérmica',
  },
  {
    label: 'Codeína VO',
    value: 'Codeína VO',
  },
  {
    label: 'Fentanil IV',
    value: 'Fentanil IV',
  },
  {
    label: 'Fentanil SC',
    value: 'Fentanil SC',
  },
  {
    label: 'Fentanil transdérmico',
    value: 'Fentanil transdérmico',
  },
  {
    label: 'Hidrocodona VO',
    value: 'Hidrocodona VO',
  },
  {
    label: 'Hidromorfona IV',
    value: 'Hidromorfona IV',
  },
  {
    label: 'Hidromorfona SC',
    value: 'Hidromorfona SC',
  },
  {
    label: 'Hidromorfona VO',
    value: 'Hidromorfona VO',
  },
  {
    label: 'Metadona (> 1.000 OMME/dia)',
    value: 'Metadona (> 1.000 OMME/dia)',
  },
  {
    label: 'Metadona (30-90 OMME/dia)',
    value: 'Metadona (30-90 OMME/dia)',
  },
  {
    label: 'Metadona (301-600 OMME/dia)',
    value: 'Metadona (301-600 OMME/dia)',
  },
  {
    label: 'Metadona (601-800 OMME/dia)',
    value: 'Metadona (601-800 OMME/dia)',
  },
  {
    label: 'Metadona (801-1.000 OMME/dia)',
    value: 'Metadona (801-1.000 OMME/dia)',
  },
  {
    label: 'Metadona (91-300 OMME/dia)',
    value: 'Metadona (91-300 OMME/dia)',
  },
  {
    label: 'Morfina IV',
    value: 'Morfina IV',
  },
  {
    label: 'Morfina SC',
    value: 'Morfina SC',
  },
  {
    label: 'Morfina VO',
    value: 'Morfina VO',
  },
  {
    label: 'Oxicodona VO',
    value: 'Oxicodona VO',
  },
  {
    label: 'Tapentadol VO',
    value: 'Tapentadol VO',
  },
  {
    label: 'Tramadol IV',
    value: 'Tramadol IV',
  },
  {
    label: 'Tramadol SC',
    value: 'Tramadol SC',
  },
  {
    label: 'Tramadol VO',
    value: 'Tramadol VO',
  },
];

const formSchema = z.object({
  drugOne: z.string().min(1, {
    message: errorsValidation.required,
  }),
  drugTwo: z.string().min(1, {
    message: errorsValidation.required,
  }),
  mg: z.string().min(1, {
    message: errorsValidation.required,
  }),
});

function ConversorOpioide() {
  const { user } = useAuth();
  const { drugOne, drugTwo, mg, setDrugOne, setDrugTwo, setMg } =
    useConversorOpioide();

  const [equivalenceCalculated, setEquivalenceCalculated] = useState<number>(0);
  const [equivalence50PercentCalculated, setEquivalence50PercentCalculated] =
    useState<number>(0);
  const [equivalence75PercentCalculated, setEquivalence75PercentCalculated] =
    useState<number>(0);
  const [durationDrugOne, setDurationDrugOne] = useState<string>('');
  const [durationDrugTwo, setDurationDrugTwo] = useState<string>('');

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    shouldUnregister: true,
    defaultValues: {
      drugOne: 'Morfina VO',
      drugTwo: 'Morfina VO',
      mg: mg.toFixed(1),
    },
  });

  const handleChangeDrugOne = useCallback(
    (value: string): void => {
      setDrugOne(value);
    },
    [setDrugOne],
  );

  const handleChangeDrugTwo = useCallback(
    (value: string): void => {
      setDrugTwo(value);
    },
    [setDrugTwo],
  );

  const handleChangeMg = useCallback(
    (value: string): void => {
      const valueParsed = Number(value.replace(',', '.'));
      setMg(valueParsed);
    },
    [setMg],
  );

  const calculateEquivalence = useCallback((): void => {
    // FÓRMULA: mg * (mg destino / mg origem)
    const equivalenceDrugOne = equivalences.find(
      (item) => item.name === drugOne,
    );
    if (!equivalenceDrugOne) {
      return;
    }
    setDurationDrugOne(equivalenceDrugOne.duration);
    const equivalenceDrugTwo = equivalences.find(
      (item) => item.name === drugTwo,
    );
    if (!equivalenceDrugTwo) {
      return;
    }
    setDurationDrugTwo(equivalenceDrugTwo.duration);
    const mgParsed = new BigNumber(mg);
    const stepOne = new BigNumber(equivalenceDrugTwo.mg).dividedBy(
      equivalenceDrugOne.mg,
    );
    const result = stepOne.multipliedBy(mgParsed);
    setEquivalenceCalculated(result.toNumber());
    setEquivalence50PercentCalculated(result.multipliedBy(0.5).toNumber());
    setEquivalence75PercentCalculated(result.multipliedBy(0.75).toNumber());
  }, [drugOne, drugTwo, mg]);

  useEffect(() => {
    calculateEquivalence();
  }, [calculateEquivalence]);

  function onSubmit(): void {
    //
  }

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'drugOne' && value.drugOne) {
        handleChangeDrugOne(value.drugOne);
      }
      if (name === 'drugTwo' && value.drugTwo) {
        handleChangeDrugTwo(value.drugTwo);
      }
      if (name === 'mg' && value.mg) {
        handleChangeMg(value.mg);
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  if (!user?.accessGranted) {
    return <Navigate replace to="/pagamento" />;
  }

  return (
    <div>
      <PageTitle hasBackButton title="Conversor opioide" />
      <Form {...form}>
        <form
          className="mx-auto max-w-3xl"
          onSubmit={form.handleSubmit(onSubmit)}>
          <div className="mx-auto block w-fit">
            <div className="mb-4 flex flex-row items-center gap-2">
              <FormField
                control={form.control}
                name="drugOne"
                render={({ field }) => (
                  <FormItem className="grid grid-cols-[3rem_19rem] items-center">
                    <FormLabel className="w-10">De:</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild className="w-full">
                        <FormControl>
                          <Button
                            className={cn(
                              'aria-invalid:border-destructive hover:text-foreground text-foreground aria-[expanded=true]:border-secondary aria-invalid:focus-visible:ring-0 w-full justify-between font-normal hover:bg-transparent aria-[expanded=true]:outline-1 aria-[expanded=true]:ring-2',
                              !field.value && 'text-muted-foreground',
                            )}
                            role="combobox"
                            variant="outline">
                            {field.value
                              ? options.find(
                                  (option) =>
                                    option.value.toString() === field.value,
                                )?.label
                              : ''}
                            <ChevronsUpDown className="ml-auto size-4 shrink-0 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="max-h-[var(--radix-popover-content-available-height)] w-[var(--radix-popover-trigger-width)] p-0">
                        <Command>
                          <CommandInput
                            className="placeholder:text-foreground/70 h-9"
                            placeholder="Buscar..."
                          />
                          <CommandList>
                            <CommandEmpty>
                              Nenhuma opção encontrada.
                            </CommandEmpty>
                            <CommandGroup>
                              {options.map((option) => (
                                <CommandItem
                                  className="data-[selected=true]:bg-tertiary"
                                  key={option.value}
                                  onSelect={() => {
                                    form.setValue(
                                      `drugOne`,
                                      option.value.toString(),
                                      {
                                        shouldValidate: true,
                                      },
                                    );
                                  }}
                                  value={option.value}>
                                  <PopoverClose className="flex w-full flex-row items-center">
                                    <Check
                                      className={cn(
                                        'mr-2 size-4',
                                        option.value.toString() === field.value
                                          ? 'opacity-100'
                                          : 'opacity-0',
                                      )}
                                    />
                                    {option.label}
                                  </PopoverClose>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </FormItem>
                )}
              />
            </div>
            <div className="mb-4 flex flex-row items-center gap-2">
              <FormField
                control={form.control}
                name="drugTwo"
                render={({ field }) => (
                  <FormItem className="grid grid-cols-[3rem_19rem] items-center">
                    <FormLabel className="w-10">Para:</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild className="w-full">
                        <FormControl>
                          <Button
                            className={cn(
                              'aria-invalid:border-destructive hover:text-foreground text-foreground aria-[expanded=true]:border-secondary aria-invalid:focus-visible:ring-0 w-full justify-between font-normal hover:bg-transparent aria-[expanded=true]:outline-1 aria-[expanded=true]:ring-2',
                              !field.value && 'text-muted-foreground',
                            )}
                            role="combobox"
                            variant="outline">
                            {field.value
                              ? options.find(
                                  (option) =>
                                    option.value.toString() === field.value,
                                )?.label
                              : ''}
                            <ChevronsUpDown className="ml-auto size-4 shrink-0 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="max-h-[var(--radix-popover-content-available-height)] w-[var(--radix-popover-trigger-width)] p-0">
                        <Command>
                          <CommandInput
                            className="placeholder:text-foreground/70 h-9"
                            placeholder="Buscar..."
                          />
                          <CommandList>
                            <CommandEmpty>
                              Nenhuma opção encontrada.
                            </CommandEmpty>
                            <CommandGroup>
                              {options.map((option) => (
                                <CommandItem
                                  className="data-[selected=true]:bg-tertiary"
                                  key={option.value}
                                  onSelect={() => {
                                    form.setValue(
                                      `drugTwo`,
                                      option.value.toString(),
                                      {
                                        shouldValidate: true,
                                      },
                                    );
                                  }}
                                  value={option.value}>
                                  <PopoverClose className="flex w-full flex-row items-center">
                                    <Check
                                      className={cn(
                                        'mr-2 size-4',
                                        option.value.toString() === field.value
                                          ? 'opacity-100'
                                          : 'opacity-0',
                                      )}
                                    />
                                    {option.label}
                                  </PopoverClose>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </FormItem>
                )}
              />
            </div>
            <div className="mb-4 flex flex-row items-center gap-2">
              <FormField
                control={form.control}
                name="mg"
                render={({ field: { ref, ...rest } }) => (
                  <FormItem className="grid grid-cols-[3rem_auto] items-center">
                    <FormLabel className="w-10">mg:</FormLabel>
                    <FormControl>
                      <NumberFormatBase
                        className="[&_input]:w-26 [&_input]:text-center"
                        customInput={Input}
                        format={(value) => formatterDecimal(value, 1)}
                        getCaretBoundary={(formattedValue) => {
                          const boundaryAry = Array.from({
                            length: formattedValue.length + 1,
                          }).map(() => true);
                          boundaryAry[0] = false;
                          boundaryAry[1] = false;
                          boundaryAry[2] = false;
                          return boundaryAry;
                        }}
                        getInputRef={ref}
                        {...rest}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>

          <Separator className="my-4" />

          <div className="mb-4 flex flex-row items-center justify-center gap-2">
            <p>
              <span className="text-tertiary text-lg font-bold">
                {mg.toFixed(1)}
              </span>{' '}
            </p>
            <Label className="text-foreground text-sm font-light">mg de</Label>
          </div>

          <div className="mb-4 flex flex-col items-center justify-center gap-2">
            <p>
              <span className="text-tertiary text-lg font-bold">{drugOne}</span>
            </p>
            <Label className="text-foreground text-sm font-light">
              equivale a
            </Label>
          </div>

          <div className="flex flex-row items-center justify-center">
            <Label className="text-foreground text-sm font-light">
              dose equianalgésica:
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button className="cursor-pointer" size="icon" variant="ghost">
                  <CircleHelp className="text-foreground" strokeWidth={2} />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full max-w-lg space-y-4">
                <h1 className="text-secondary text-center font-medium">
                  Dose equianalgésica
                </h1>
                <p className="text-foreground flex-1 text-sm">
                  A dose equianalgésica deve ser reduzida em 25% para pacientes
                  com bom performance status e 50% para pacientes com
                  performance status pior.
                </p>
              </PopoverContent>
            </Popover>
          </div>

          <div className="mb-4 flex flex-row items-center justify-center gap-2">
            <p>
              <span className="text-tertiary text-lg font-bold">
                {equivalenceCalculated.toFixed(1)}
              </span>{' '}
            </p>
            <Label className="text-foreground text-sm font-light">mg de</Label>
          </div>

          <div className="mb-4 flex flex-col items-center justify-center gap-2">
            <p>
              <span className="text-tertiary text-lg font-bold">{drugTwo}</span>
            </p>
          </div>

          <div className="flex flex-row items-center justify-center">
            <Label className="text-foreground text-sm font-light">
              dose equianalgésica reduzida:
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button className="cursor-pointer" size="icon" variant="ghost">
                  <CircleHelp className="text-foreground" strokeWidth={2} />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full max-w-lg space-y-4">
                <h1 className="text-secondary text-center font-medium">
                  Dose equianalgésica
                </h1>
                <p className="text-foreground flex-1 text-sm">
                  A dose equianalgésica deve ser reduzida em 25% para pacientes
                  com bom performance status e 50% para pacientes com
                  performance status pior.
                </p>
              </PopoverContent>
            </Popover>
          </div>

          <div className="mb-4 flex flex-row items-center justify-center gap-2">
            <p>
              <span className="text-tertiary text-lg font-bold">
                {equivalence50PercentCalculated.toFixed(1)}
              </span>{' '}
              <Label className="text-foreground text-sm font-light">a</Label>{' '}
              <span className="text-tertiary text-lg font-bold">
                {equivalence75PercentCalculated.toFixed(1)}
              </span>
            </p>
            <Label className="text-foreground text-sm font-light">mg de</Label>
          </div>

          <div className="mb-8 flex flex-col items-center justify-center gap-2">
            <p>
              <span className="text-tertiary text-lg font-bold">{drugTwo}</span>
            </p>
          </div>

          <div className="mb-8 flex flex-col items-center justify-center gap-2">
            <p>
              <span className="text-lg font-bold">Meia-vida</span>
            </p>
            <Label className="text-foreground text-sm font-light">{`${drugOne} ${durationDrugOne}`}</Label>
            <Label className="text-foreground text-sm font-light">{`${drugTwo} ${durationDrugTwo}`}</Label>
          </div>

          <p className="text-foreground text-center text-base font-light">
            <span className="text-lg font-bold">Observações</span>
          </p>
          <p className="text-foreground mt-4 text-center text-xs font-light">
            - O equivalente oral de miligrama de morfina (OMME) da metadona não
            é linear. Portanto, a equivalência da metadona com 30 mg/dia de
            morfina VO é diferente da equivalência de 100mg/dia de morfina VO.
          </p>
          <p className="text-foreground mt-4 text-center text-xs font-light">
            - Algumas referência recomendam reduzir a dose equianalgésica da
            metadona em 75–90%.
          </p>

          <p className="text-foreground mt-8 text-center text-base font-light">
            <span className="text-lg font-bold">Referência:</span>
          </p>
          <p className="text-foreground mt-4 text-center text-xs font-light">
            - US Department of Health and Human Services/Centers for Disease
            Control and Prevention. CDC Clinical Practice Guideline for
            Prescribing Opioids for Pain — United States, 2022. MMWR / November
            4, 2022.
          </p>
          <p className="text-foreground mt-4 text-center text-xs font-light">
            - Wiermann et al. Consenso Brasileiro sobre Manejo da Dor
            Relacionada ao Câncer. Revista Brasileira de Oncologia Clínica, Vol.
            10, no 38, 2014.
          </p>
          <p className="text-foreground mt-4 text-center text-xs font-light">
            - Pedro et al. Manual de rotação de opióides. 3ª Edição Junho 2017.
          </p>
        </form>
      </Form>
    </div>
  );
}

export default ConversorOpioide;
